import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeoTitle } from "@/lib/useSeoTitle";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Flame, MapPin, Users, Compass, ArrowLeft, Sparkles } from "lucide-react";

/**
 * WorldDetail — crawlable single-world page at /worlds/:slug.
 *
 * Fetches the featured worlds list once on mount, finds the matching
 * slug, and renders a full lore page with H1, cover art, blurb,
 * scenario paragraph, race/class chips, and a CTA to start playing.
 * Unknown slugs 404-redirect back to /worlds.
 *
 * SEO strategy: modern crawlers (Googlebot, Bingbot, GPTBot, ClaudeBot,
 * PerplexityBot) all execute JS by 2026. This route is listed in
 * sitemap.xml so crawlers find it, then render it and read the title/H1/
 * description tags set by useSeoTitle. The lore prose lives visible in
 * the DOM once loaded so it's picked up by both search and LLM crawlers.
 */
export default function WorldDetail() {
  const { slug } = useParams();
  const [worlds, setWorlds] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    api
      .get("/worlds/featured")
      .then((r) => { if (!cancelled) setWorlds(r.data || []); })
      .catch(() => { if (!cancelled) setWorlds([]); });
    return () => { cancelled = true; };
  }, []);

  const world = Array.isArray(worlds) ? worlds.find((w) => w.id === slug) : undefined;

  // Loading — render nothing but a lightweight skeleton so we don't
  // flash the 404 redirect while the API is still in flight.
  useSeoTitle({
    title: world
      ? `${world.title} — ${world.setting} | TalesTalesTales`
      : "Story world | TalesTalesTales",
    description: world
      ? `Play ${world.title} — a ${world.setting} story world with ${world.tone.toLowerCase()} tone on TalesTalesTales. ${world.blurb || ""}`.slice(0, 300)
      : "A featured story world on TalesTalesTales — an interactive AI Novel.",
    path: `/worlds/${slug}`,
  });

  if (worlds === null) {
    return (
      <div className="min-h-screen bg-[#0f0d11]">
        <Nav />
        <main className="max-w-4xl mx-auto px-4 py-16 text-center">
          <p className="font-ui text-amber-200/50 text-xs uppercase tracking-widest">
            Unfurling scroll…
          </p>
        </main>
      </div>
    );
  }

  if (!world) {
    // Bounce unknown slugs back to /worlds — semantically better than 404
    // because /worlds itself is crawlable and lists everything.
    return <Navigate to="/worlds" replace />;
  }

  const races = Array.isArray(world.allowed_races) ? world.allowed_races : [];
  const classes = Array.isArray(world.allowed_classes) ? world.allowed_classes : [];

  return (
    <div data-testid="world-detail-page" className="min-h-screen bg-[#0f0d11]">
      <Nav />
      <main id="main-content" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20">
        <Link
          to="/worlds"
          data-testid="world-back-link"
          className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-widest text-amber-300/70 hover:text-amber-200 mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All story worlds
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Cover art */}
          {world.image && (
            <figure className="parchment gold-border rounded-xl overflow-hidden">
              <img
                src={world.image}
                alt={`${world.title} — ${world.setting} cover art`}
                className="w-full h-64 sm:h-80 object-cover"
                loading="eager"
                data-testid="world-cover-image"
              />
              <figcaption className="px-4 py-2 font-ui text-[10px] uppercase tracking-widest text-amber-300/60">
                {world.setting} · {world.tone}
              </figcaption>
            </figure>
          )}

          {/* Header + lore */}
          <div>
            <p className="font-ui text-[10px] uppercase tracking-[0.3em] text-amber-400/80 mb-3">
              Story world
            </p>
            <h1
              data-testid="world-h1"
              className="font-display text-3xl sm:text-4xl lg:text-5xl text-amber-50 leading-tight mb-4"
            >
              {world.title}
            </h1>
            {world.blurb && (
              <p className="font-body text-amber-100/85 text-base sm:text-lg leading-relaxed mb-5">
                {world.blurb}
              </p>
            )}
            {world.scenario && (
              <p className="font-body italic text-amber-200/75 leading-relaxed border-l-2 border-amber-500/30 pl-3 mb-6">
                &ldquo;{world.scenario}&rdquo;
              </p>
            )}

            <div className="flex flex-wrap gap-3 mb-6">
              <Link to={`/register?world=${encodeURIComponent(world.id)}`}>
                <Button
                  data-testid="world-start-btn"
                  className="bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui h-11 px-5"
                >
                  <Flame className="w-4 h-4 mr-2" /> Start in this world
                </Button>
              </Link>
              <Link to="/trial">
                <Button
                  data-testid="world-trial-btn"
                  variant="outline"
                  className="border-amber-500/40 text-amber-200 hover:bg-amber-900/20 font-ui h-11 px-5 bg-transparent"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Try 5 turns free
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Feature bullets — describes what playing this world will actually be like */}
        <section aria-labelledby="world-features-heading" className="mt-12">
          <h2
            id="world-features-heading"
            className="font-display text-2xl text-amber-50 mb-4"
          >
            What playing {world.title} feels like
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { icon: Compass, label: "Setting", body: `${world.setting} — the AI narrator writes in a voice tuned for this world.` },
              { icon: MapPin, label: "Tone", body: `${world.tone}. Every scene picks vocabulary and pacing to match.` },
              { icon: Users, label: "Playable heroes", body: races.length ? `Choose from ${races.slice(0, 6).join(", ")}${races.length > 6 ? "…" : ""}.` : "A wide selection of races and classes fits the setting." },
              { icon: Sparkles, label: "Memory Codex", body: "Every NPC, thread and milestone in this world is remembered across chapters." },
            ].map((f, i) => (
              <li
                key={i}
                data-testid={`world-feature-${i}`}
                className="parchment gold-border rounded-lg p-4 flex gap-3"
              >
                <f.icon className="w-5 h-5 text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-ui text-[10px] uppercase tracking-widest text-amber-300/70">{f.label}</p>
                  <p className="font-body text-sm text-amber-100/80 leading-relaxed">{f.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Race + Class chips */}
        {(races.length > 0 || classes.length > 0) && (
          <section aria-labelledby="world-hero-heading" className="mt-10">
            <h2 id="world-hero-heading" className="font-display text-2xl text-amber-50 mb-4">
              Forge your hero
            </h2>
            {races.length > 0 && (
              <div className="mb-3">
                <p className="font-ui text-[10px] uppercase tracking-widest text-amber-300/70 mb-2">Races</p>
                <div className="flex flex-wrap gap-2">
                  {races.map((r) => (
                    <span key={r} className="parchment gold-border rounded-full px-3 py-1 font-ui text-[11px] text-amber-100/85">
                      {r}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {classes.length > 0 && (
              <div>
                <p className="font-ui text-[10px] uppercase tracking-widest text-amber-300/70 mb-2">Classes</p>
                <div className="flex flex-wrap gap-2">
                  {classes.map((c) => (
                    <span key={c} className="parchment gold-border rounded-full px-3 py-1 font-ui text-[11px] text-amber-100/85">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Cross-links back into the funnel */}
        <section aria-labelledby="world-explore-heading" className="mt-14 text-center">
          <h2 id="world-explore-heading" className="font-display text-xl text-amber-50 mb-4">
            Explore more
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/worlds" data-testid="world-cross-worlds" className="parchment gold-border rounded-full px-4 py-2 font-ui text-xs uppercase tracking-widest text-amber-200">
              All worlds
            </Link>
            <Link to="/how-to-play" className="parchment gold-border rounded-full px-4 py-2 font-ui text-xs uppercase tracking-widest text-amber-200">
              How to play
            </Link>
            <Link to="/pricing" className="parchment gold-border rounded-full px-4 py-2 font-ui text-xs uppercase tracking-widest text-amber-200">
              Pricing
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
